import { HeartHandshake, ShieldCheck, Sparkles } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHero } from "@/components/PageHero";
import { Reveal, SectionHeading } from "@/components/Reveal";
import { CTASection } from "@/components/CTASection";
import { IMAGES, STEPS } from "@/data/content";

const PHILOSOPHY = [
  { icon: Sparkles, title: "Shared interests first", text: "Gardening, football, old Glasgow, baking, big band music — we match on the things that make conversation easy." },
  { icon: HeartHandshake, title: "Personality over postcode", text: "A quiet soul gets a gentle presence; a storyteller gets someone who loves to listen. Chemistry beats geography." },
  { icon: ShieldCheck, title: "Vetted before the doorstep", text: "PVG checked and personally interviewed, every befriender. Only people we'd happily send to our own gran." },
];

export default function HowItWorks() {
  return (
    <>
      <Seo
        title="How it works — Brightside Befriending Services, Glasgow"
        siteName="Brightside Befriending Services"
        description="From a free consultation to thoughtful matching and a relaxed first visit — how Brightside introduces the right befriender to your family."
      />
      <PageHero
        eyebrow="How it works"
        lines={["From first hello", "to familiar face."]}
        text="No forms in triplicate, no waiting lists. Just four gentle steps between today and a brighter weekly routine."
      />

      <section className="px-4 pb-24 sm:px-6 lg:px-8" data-testid="journey-section">
        <div className="mx-auto max-w-7xl space-y-20">
          {STEPS.map((step, i) => (
            <div key={step.num} className={`grid items-center gap-10 lg:grid-cols-2 ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
              <Reveal>
                <div className="relative rounded-[2.5rem] border border-sand bg-white p-10 sm:p-14">
                  <span className="pointer-events-none absolute -top-8 right-8 font-serif text-[8rem] font-semibold leading-none text-sage-light/70">{step.num}</span>
                  <p className="text-xs font-semibold uppercase tracking-[0.24em] text-terracotta">Step {step.num} of 4</p>
                  <h2 className="relative mt-3 font-serif text-2xl text-forest sm:text-3xl">{step.title}</h2>
                  <p className="relative mt-4 max-w-md text-base leading-relaxed text-pebble">{step.text}</p>
                </div>
              </Reveal>
              <Reveal delay={0.12}>
                <div className="photo-frame overflow-hidden rounded-[2.5rem] shadow-[0_30px_60px_rgba(28,56,41,0.16)]">
                  <img
                    src={[IMAGES.tea, IMAGES.hero, IMAGES.walk, IMAGES.park][i]}
                    alt={["Sharing tea and conversation at home", "A befriender and client laughing together", "A gentle walk together in the park", "An outing in the garden"][i]}
                    className="h-72 w-full object-cover sm:h-80"
                  />
                </div>
              </Reveal>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-white/50 px-4 py-24 sm:px-6 lg:px-8" data-testid="matching-philosophy">
        <div className="mx-auto max-w-7xl">
          <SectionHeading eyebrow="Our matching philosophy" title="The right person changes everything" />
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {PHILOSOPHY.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.1}>
                <article className="h-full rounded-3xl border border-sand bg-white p-8 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_50px_rgba(28,56,41,0.12)]">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-sage-tint text-sage">
                    <p.icon className="h-5 w-5" />
                  </span>
                  <h3 className="mt-6 font-serif text-xl text-forest">{p.title}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-pebble">{p.text}</p>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="px-4 py-24 sm:px-6 lg:px-8" data-testid="peace-of-mind">
        <Reveal>
          <div className="relative mx-auto max-w-4xl overflow-hidden rounded-[2.5rem] bg-sage-tint p-10 text-center sm:p-16">
            <h2 className="font-serif text-2xl text-forest sm:text-4xl">The family peace-of-mind promise</h2>
            <p className="mx-auto mt-5 max-w-2xl text-base leading-relaxed text-forest/75">
              After every visit, families receive a short update — how the visit went, what you chatted about, and anything we noticed. If something ever concerns us, you'll hear it from us first, the same day.
            </p>
          </div>
        </Reveal>
      </section>

      <CTASection title="Step one is just a conversation" text="Book your free consultation and we'll take it from there — gently, and at your pace." />
    </>
  );
}
